import toast from "react-hot-toast";
import { promptTopUp } from "../../pages/intelligence/v2/lib/paywall";
import { confirmCreditChargeModal } from "./creditChargeModal";
import { formatRupees } from "./formatRupees";
import { msg } from "./messages";

/**
 * "Always confirm first" credit-charge gate for a paid action, driven by a
 * backend quote { willCharge, enough, cost, balance, chargedCount }:
 *   - free (no charge)   → returns true (proceed silently)
 *   - not enough credits → opens the Add-credits modal, returns false
 *   - enough             → shows a confirm dialog, returns the user's choice
 *
 * Server-authoritative: the backend re-charges on the actual action, this is
 * only the pre-confirmation. Reuses the same top-up modal as the crowd request
 * flow (promptTopUp) and the styled confirm toast.
 *
 * @param {Function} dispatch redux dispatch
 * @param {object} quote backend quote
 * @param {{ label?: string, unit?: string, confirmLabel?: string }} [opts]
 * @returns {Promise<boolean>} whether the caller should proceed with the action
 */
export async function confirmCreditCharge(dispatch, quote, opts = {}) {
  const cost = Number(quote && quote.cost) || 0;
  if (!quote || !quote.willCharge || cost <= 0) return true; // free → proceed

  const balance = Number(quote.balance) || 0;
  if (!quote.enough || balance < cost) {
    promptTopUp(dispatch, Math.max(0, cost - balance));
    toast.error(msg("PAYMENT.NEED_BALANCE", { amount: formatRupees(cost) }));
    return false;
  }

  // Reached the free limit + can afford it → show a prominent popup that spells
  // out the deduction. Never charge without this explicit confirmation.
  return confirmCreditChargeModal({
    cost,
    balance,
    freeLimit: Number(quote.freeLimit) || 0,
    unit: opts.unit || "item",
    label: opts.label || "This action",
    confirmLabel: opts.confirmLabel,
  });
}

/** Detect a backend "insufficient credits" rejection so a caller's catch can
 *  open the top-up modal as a fallback (rare race after the pre-check). */
export function isInsufficientCredits(err) {
  const msg =
    err?.response?.data?.status?.message || err?.message || String(err || "");
  return /insufficient credits/i.test(msg);
}

/** The backend refused to charge because the client didn't confirm (HTTP 402).
 *  A backstop that ONLY fires if the proactive popup was skipped (e.g. the quote
 *  call failed) — so a charge is NEVER applied without confirmation. Accepts an
 *  axios error, a thunk rejection payload ({ status }), or a raw status. */
export function needsChargeConfirmation(errOrRes) {
  const status =
    errOrRes?.response?.status ??
    errOrRes?.status ??
    errOrRes?.payload?.status;
  return Number(status) === 402;
}
